import PropTypes from "prop-types"
import React, { useEffect, useMemo, useRef, useState } from "react"

// Import Scrollbar (not used directly here; container uses data-simplebar)

import { withRouter, Link } from "react-router-dom"
import { useDispatch } from "react-redux"

// i18n
import { withTranslation } from "react-i18next"
import {
  getRoleUser,
  getValueEnum,
  getCurrentUser,
  currencyFormat,
} from "helpers/service_helper"
import { rolesType } from "common/enums/rolesType"
import DialogBank from "components/Common/DialogBank"
import DialogOwnerBank from "components/Common/DialogOwnerBank"
import { Alert, Menu, Tooltip, Divider, Avatar, Typography, Space, Button, Popover, Card, Badge } from "antd"
import { 
  UserOutlined, 
  LogoutOutlined, 
  WalletOutlined, 
  SafetyCertificateOutlined, 
  EnvironmentOutlined, 
  IdcardOutlined,
  DashboardOutlined,
  SettingOutlined,
  FolderOutlined,
  FileAddOutlined,
  FileTextOutlined,
  ProfileOutlined,
  UnorderedListOutlined,
  PlayCircleOutlined,
  CommentOutlined,
  FileSearchOutlined,
  BarChartOutlined,
  DatabaseOutlined,
  TeamOutlined,
  AppstoreOutlined,
  BlockOutlined,
  BellOutlined,
  BranchesOutlined,
  RobotOutlined,
  ScheduleOutlined,
  ToolOutlined,
  MoneyCollectOutlined,
  BankOutlined
} from "@ant-design/icons"
import waterHeaterIcon from "assets/icons/water-heater.svg"
import { getApiUrlUserFiles } from "common/global"
import { EngWorkReader } from "services/EngWorkReader"
import { changeSidebarType } from "../../store/actions"

// Icon mapping from string to component
const WaterHeaterIcon = () => (
  <img  src={waterHeaterIcon} alt="water heater" style={{ width: 16, height: 16, marginLeft: 5 }} />
)

const iconMap = {
  DashboardOutlined,
  SettingOutlined,
  FolderOutlined,
  FileAddOutlined,
  FileTextOutlined,
  ProfileOutlined,
  UnorderedListOutlined,
  PlayCircleOutlined,
  WalletOutlined,
  CommentOutlined,
  FileSearchOutlined,
  BarChartOutlined,
  DatabaseOutlined,
  TeamOutlined,
  UserOutlined,
  EnvironmentOutlined,
  AppstoreOutlined,
  BlockOutlined,
  BellOutlined,
  BranchesOutlined,
  RobotOutlined,
  ScheduleOutlined,
  ToolOutlined,
  SafetyCertificateOutlined,
  IdcardOutlined,
  WaterHeater: WaterHeaterIcon,
}

const SidebarContent = props => {
  const [listMenu, setListMenu] = useState([])
  const [openKeys, setOpenKeys] = useState([])
  const [selectedKeys, setSelectedKeys] = useState([])
  const collapsed = !!props.collapsed
  const lastOpenKeysRef = useRef([])
  const { userInfo } = props
  const dispatch = useDispatch()

  // Initialize menu data from props once
  useEffect(() => {
    if (!listMenu || listMenu.length === 0) {
      setListMenu(props.appMenus || [])
    }
  }, [props.appMenus])

  // Preserve open state across collapse toggle (controlled via prop)
  useEffect(() => {
    if (collapsed) {
      lastOpenKeysRef.current = openKeys
    } else {
      if (lastOpenKeysRef.current && lastOpenKeysRef.current.length) {
        setOpenKeys(lastOpenKeysRef.current)
      }
    }
  }, [collapsed])

  // Build helpers to find keys by path
  const flatItems = useMemo(() => {
    const items = []
    const walk = (nodes, parents = []) => {
      if (!nodes) return
      nodes.forEach(node => {
        const key = node.path || String(node.id)
        items.push({ key, node, parents: [...parents] })
        if (node.children && node.children.length) {
          walk(node.children, [...parents, key])
        }
      })
    }
    walk(listMenu)
    return items
  }, [listMenu])

  const findBestMatch = pathName => {
    if (!pathName) return null
    // Prefer the deepest path that is a prefix of the current location
    const matches = flatItems.filter(x => pathName.startsWith(x.key))
    if (matches.length === 0) return null
    // Deepest = longest key length
    return matches.reduce((a, b) => (a.key.length >= b.key.length ? a : b))
  }

  // Update selected/open keys on route or menu changes
  useEffect(() => {
    const pathName = props.location?.pathname || "/"
    const match = findBestMatch(pathName)
    if (match) {
      setSelectedKeys([match.key])
      setOpenKeys(match.parents)
      // Optionally ensure selected item visibility within sidebar scroll container
      // Rely on native browser scroll behavior or SimpleBar auto-init on the wrapper
    } else {
      setSelectedKeys([])
      setOpenKeys([])
    }
  }, [props.location?.pathname, flatItems])

  const onOpenChange = keys => {
    setOpenKeys(keys)
  }

  // If needed later, we can map keys to nodes. Currently unused.

  const onMenuClick = ({ key, domEvent }) => {
    // If user clicked directly on an anchor (supports right-click open in new tab),
    // let the anchor handle navigation.
    if (domEvent?.target && domEvent.target.closest && domEvent.target.closest('a')) {
      // Still close Drawer on mobile on normal left-clicks handled by the anchor
      // We also handle Drawer close in the Link onClick below for reliability.
      return
    }
    // Standard pattern: navigate when key is a route path (leaf items use path as key)
    if (key === "__profile") return
    const path = typeof key === "string" ? key : null
    if (path && path.startsWith("/") && props.history && props.location?.pathname !== path) {
      props.history.push(path)
    }
    // Close the mobile Drawer after navigation
    if (window.innerWidth <= 992) {
      dispatch(changeSidebarType("condensed", true))
    }
  }

  const onLogout = () => {
    if (props.history) {
      props.history.push("/logout")
    }
    if (window.innerWidth <= 992) {
      dispatch(changeSidebarType("condensed", true))
    }
  }

  // Build Antd Menu items prop (standard pattern) - without profile header
  const menuItems = useMemo(() => {
    const build = nodes =>
      (nodes || [])
        .filter(node => !node.hide) // Filter out hidden items
        .map(node => {
        const key = node.path || String(node.id)
        // node.icon is now a string (e.g., "DashboardOutlined"), map it to the component
        const IconComponent = node.icon ? iconMap[node.icon] : undefined
        const icon = IconComponent ? <IconComponent style={{ fontSize: 16 }} /> : undefined
        if (node.children && node.children.length > 0) {
          const filteredChildren = build(node.children)
          // Don't render parent if all children are hidden
          if (filteredChildren.length === 0) return null
          return {
            key,
            icon,
            label: props.t(node.title),
            title: props.t(node.title),
            children: filteredChildren,
          }
        }
        // Leaf: render label as a Link so right-click works (open in new tab)
        const labelNode = (
          <Link
            to={key}
            draggable={false}
            className="p-0"
            onClick={() => {
              if (window.innerWidth <= 992) {
                // Close Drawer on mobile after navigation
                dispatch(changeSidebarType("condensed", true))
              }
            }}
          >
            {props.t(node.title)}
          </Link>
        )
        return { key, icon, label: labelNode, title: props.t(node.title) }
      })
      .filter(Boolean) // Remove null items (parents with no visible children)
    return build(listMenu)
  }, [listMenu, props.t])

  // Profile header extracted from menu for better layout
  const profileDetails = (
    <div style={{ fontSize: 13 }}>
      {getCurrentUser().role === "Engineer" && (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {userInfo?.solarCreditDate && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                background: '#e6f7ff',
                borderRadius: 6,
                border: '1px solid #91d5ff',
              }}
            >
              <SafetyCertificateOutlined style={{ color: '#1890ff' }} />
              <span>اعتبار پروانه: <strong>{userInfo.solarCreditDate}</strong></span>
            </div>
          )}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                background: userInfo?.insuranceExpired ? '#fff1f0' : '#e6f7ff',
                borderRadius: 6,
                border: `1px solid ${userInfo?.insuranceExpired ? '#ffa39e' : '#91d5ff'}`,
              }}
            >
              <SafetyCertificateOutlined style={{ color: userInfo?.insuranceExpired ? '#ff4d4f' : '#1890ff' }} />
              <span>اعتبار بیمه: <strong style={{ color: userInfo?.insuranceExpired ? '#ff4d4f' : undefined }}>{userInfo?.solarInsuranceExpirationDate}</strong></span>
            </div>
          {userInfo?.engGradeName && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                background: '#f6ffed',
                borderRadius: 6,
                border: '1px solid #b7eb8f',
              }}
            >
              <IdcardOutlined style={{ color: '#52c41a' }} />
              <span>{userInfo.engGradeName}</span>
            </div>
          )}
          {userInfo?.cityName && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                background: '#fff7e6',
                borderRadius: 6,
                border: '1px solid #ffd591',
              }}
            >
              <EnvironmentOutlined style={{ color: '#fa8c16' }} />
              <span>شهر: <strong>{userInfo.cityName}</strong></span>
            </div>
          )}
            {userInfo?.naCode && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                background: '#fff7e6',
                borderRadius: 6,
                border: '1px solid #ffd591',
              }}
            >
              <BankOutlined style={{ color: '#fa8c16' }} />
              <span>کارکرد 1404 به دارایی: <strong><EngWorkReader naCode={userInfo.naCode} /></strong></span>
            </div>
          )}
        </Space>
      )}
      {getCurrentUser().role === "Administrator" && (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid #91d5ff',
            }}
          >
            <Space>
              <WalletOutlined style={{ color: '#1890ff', fontSize: 16 }} />
              <span>کیف پول</span>
            </Space>
            <Typography.Text strong style={{ color: '#1890ff' }}>
              {currencyFormat(props?.userBalance)}
            </Typography.Text>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid #b7eb8f',
            }}
          >
            <span>کل واریزی مهندسین</span>
            <Typography.Text strong style={{ color: '#52c41a' }}>
              {currencyFormat(userInfo?.sumAmountEngPayment)}
            </Typography.Text>
          </div>
        </Space>
      )}
      {getCurrentUser().role === "Executor" && (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid #ffd591',
            }}
          >
            <Space>
              <WalletOutlined style={{ color: '#fa8c16', fontSize: 16 }} />
              <span>موجودی</span>
            </Space>
            <Typography.Text strong style={{ color: '#fa8c16' }}>
              {currencyFormat(props?.userBalance)}
            </Typography.Text>
          </div>
          <DialogBank />
        </Space>
      )}
    </div>
  )

  return (
    <div style={{backgroundColor: "#FFFFFF"}}>
      {/* Profile header outside the Menu for best layout */}
      {collapsed ? (
        /* حالت جمع شده - فقط آواتار و دکمه‌های کوچک */
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '16px 8px',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <Popover
            content={
              <div style={{ minWidth: 200 }}>
                <div style={{ textAlign: 'center', marginBottom: 12 }}>
                  <Avatar
                    src={`${getApiUrlUserFiles(getCurrentUser().sid + "/PicPersonal.jpg")}`}
                    alt={props?.userInfo?.firstName}
                    size={64}
                    style={{
                      border: '3px solid #1890ff',
                      boxShadow: '0 4px 12px rgba(24,144,255,0.3)',
                    }}
                  />
                  <Typography.Title level={5} style={{ margin: '8px 0 4px' }}>
                    {props?.userInfo?.firstName}
                  </Typography.Title>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {(getRoleUser() || [])
                      .map(r => getValueEnum(rolesType, r).value)
                      .join("، ")}
                  </Typography.Text>
                </div>
                <Divider style={{ margin: '12px 0' }} />
                {profileDetails}
              </div>
            }
            placement="rightTop"
            trigger={["click", "hover"]}
          >
            <Badge dot status="success" offset={[-4, 36]}>
              <Avatar
                src={`${getApiUrlUserFiles(getCurrentUser().sid + "/PicPersonal.jpg")}`}
                alt={props?.userInfo?.firstName}
                size={44}
                style={{
                  border: '2px solid #1890ff',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              />
            </Badge>
          </Popover>

          <Tooltip title={props.t ? props.t("Logout") : "خروج"} placement="right">
            <Button
              size="small"
              type="text"
              danger
              onClick={onLogout}
              style={{ marginTop: 8 }}
              icon={<LogoutOutlined />}
            />
          </Tooltip>
        </div>
      ) : (
        /* حالت باز - کارت پروفایل کامل */
        <div
          style={{
            padding: '16px',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          {/* هدر پروفایل */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 12,
            }}
          >
            <Badge dot status="success" offset={[-4, 44]}>
              <Avatar
                src={`${getApiUrlUserFiles(getCurrentUser().sid + "/PicPersonal.jpg")}`}
                alt={props?.userInfo?.firstName}
                size={52}
                style={{
                  border: '3px solid #1890ff',
                  boxShadow: '0 4px 12px rgba(24,144,255,0.25)',
                }}
              />
            </Badge>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Typography.Text
                strong
                style={{
                  fontSize: 15,
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {props?.userInfo?.firstName}
              </Typography.Text>
              <Typography.Text
                type="secondary"
                style={{
                  fontSize: 11,
                  display: 'block',
                }}
              >
                {(getRoleUser() || [])
                  .map(r => getValueEnum(rolesType, r).value)
                  .join("، ")}
              </Typography.Text>
            </div>
            <Tooltip title={props.t ? props.t("Logout") : "خروج"}>
              <Button
                size="small"
                type="text"
                danger
                onClick={onLogout}
                icon={<LogoutOutlined />}
              />
            </Tooltip>
          </div>

          {/* جزئیات پروفایل */}
          <Card
            size="small"
            style={{
              borderRadius: 10,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: 'none',
            }}
            styles={{ body: { padding: 12 } }}
          >
            {profileDetails}
          </Card>
        </div>
      )}

      <Menu
        mode="inline"
        selectedKeys={selectedKeys}
        openKeys={collapsed ? undefined : openKeys}
        onOpenChange={collapsed ? undefined : onOpenChange}
        onClick={onMenuClick}
        inlineCollapsed={collapsed}
        triggerSubMenuAction="click"
        items={menuItems}
        style={{
          borderInlineEnd: 'none',
        }}
      />
    </div>
  )
}


export default withRouter(withTranslation()(SidebarContent))
